import React, { useState, useEffect } from 'react';
import {
  Building2, Users, DollarSign, MessageSquare,
  ArrowUpRight, Sparkles, BarChart3, CheckCircle,
  Clock, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const OwnerDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/analytics/owner-dashboard')
      .then(setData)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="owner-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" />
      </div>
    );
  }

  if (!data) return null;

  const stats = [
    { name: 'Total Facilities', value: data.facilities?.total ?? 0, icon: Building2 },
    { name: 'Active Users', value: data.users?.active ?? 0, icon: Users },
    { name: 'Monthly Revenue', value: `$${(data.revenue?.monthly ?? 0).toLocaleString()}`, icon: DollarSign },
    { name: 'Safety Events', value: data.safety_events?.total ?? 0, icon: MessageSquare },
  ];

  const topFacilities = data.top_facilities || [];

  return (
    <div className="space-y-8" data-testid="owner-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Owner Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's what's happening across NikoSafe Connect.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Export Report
          </Button>
          <Button onClick={() => navigate('/owner/facilities')} className="btn-primary gap-2" data-testid="add-facility-btn">
            <Building2 className="w-4 h-4" />
            Manage Facilities
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`owner-stat-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--niko-teal)]/10 to-[var(--niko-green)]/10 flex items-center justify-center">
                <stat.icon className="w-6 h-6 text-[var(--niko-teal)]" />
              </div>
              <ArrowUpRight className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500 mt-1">{stat.name}</p>
          </div>
        ))}
      </div>

      {/* Facilities Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Facilities</h2>
            <p className="text-sm text-gray-500">Top healthcare organizations</p>
          </div>
          <Button variant="ghost" className="text-[var(--niko-teal)]" onClick={() => navigate('/owner/facilities')}>
            View All
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Facility</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Type</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Users</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Revenue</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topFacilities.map((facility) => (
                <tr key={facility.id} className="hover:bg-gray-50 transition-colors" data-testid="owner-facility-row">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[var(--niko-teal)]/10 to-[var(--niko-green)]/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-[var(--niko-teal)]" />
                      </div>
                      <span className="font-medium text-gray-900">{facility.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{facility.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{(facility.user_count ?? 0).toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">${(facility.monthly_rate ?? 0).toLocaleString()}/mo</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      facility.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {facility.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {facility.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue Highlight */}
      <div className="bg-gradient-to-br from-[#0a1628] to-[#1a3a4a] rounded-2xl p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">Revenue Summary</h2>
            <p className="text-white/60 text-sm">Platform financial overview</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-5">
            <p className="text-white/80 text-sm font-medium">Monthly Revenue</p>
            <p className="text-3xl font-bold text-white mt-2" data-testid="monthly-revenue">
              ${(data.revenue?.monthly ?? 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-5">
            <p className="text-white/80 text-sm font-medium">Annual Projected</p>
            <p className="text-3xl font-bold text-[var(--niko-green)] mt-2" data-testid="annual-revenue">
              ${(data.revenue?.annual_projected ?? 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-5">
            <p className="text-white/80 text-sm font-medium">Active Facilities</p>
            <p className="text-3xl font-bold text-white mt-2">
              {data.facilities?.active ?? 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
